import { Data, Effect } from "effect";

export class RefreshUnavailable extends Data.TaggedError(
  "RefreshUnavailable",
) {}

interface RefreshBrowser {
  readonly location: Pick<Location, "href" | "reload">;
  readonly navigator: Pick<Navigator, "onLine"> & {
    readonly serviceWorker?: ServiceWorkerContainer;
  };
  readonly fetch: typeof fetch;
}

function inWorkerScope(scope: string, href: string) {
  const current = new URL(href);
  const worker = new URL(scope);
  return (
    current.origin === worker.origin &&
    current.pathname.startsWith(worker.pathname)
  );
}

/** A retained controller must not turn a fresh probe into an offline cache hit. */
async function networkOnlyFetch(browser: RefreshBrowser, signal: AbortSignal) {
  if (!browser.navigator.serviceWorker?.controller)
    return { fetch: browser.fetch, close: () => {} };

  const frame = document.createElement("iframe");
  frame.hidden = true;
  frame.tabIndex = -1;
  frame.setAttribute("aria-hidden", "true");
  // Keep same-origin SDK access, but never execute the shell's scripts.
  frame.setAttribute("sandbox", "allow-same-origin");
  const address = new URL("/index.html", browser.location.href);
  address.searchParams.set("refresh-preflight", crypto.randomUUID());
  frame.src = address.href;
  try {
    await new Promise<void>((resolve, reject) => {
      let done = false;
      const finish = (loaded: boolean) => {
        if (done) return;
        done = true;
        frame.removeEventListener("load", loadedFrame);
        frame.removeEventListener("error", failedFrame);
        signal.removeEventListener("abort", failedFrame);
        if (loaded) resolve();
        else reject(new RefreshUnavailable());
      };
      const loadedFrame = () => finish(true);
      const failedFrame = () => finish(false);
      frame.addEventListener("load", loadedFrame);
      frame.addEventListener("error", failedFrame);
      signal.addEventListener("abort", failedFrame, { once: true });
      if (signal.aborted) return failedFrame();
      try {
        document.body.append(frame);
      } catch {
        failedFrame();
      }
    });
    const target = frame.contentWindow;
    if (!target || target.navigator.serviceWorker?.controller)
      throw new RefreshUnavailable();
    return {
      fetch: target.fetch.bind(target),
      close: () => frame.remove(),
    };
  } catch (error) {
    frame.remove();
    throw error;
  }
}

/** Check the fresh shell and its declared boot resources without executing another app. */
function prepareUncontrolledPage(browser: RefreshBrowser) {
  return Effect.tryPromise({
    try: async (signal) => {
      const network = await networkOnlyFetch(browser, signal);
      try {
        const shell = new URL("/index.html", browser.location.href);
        const read = async (url: URL, type: "html" | "script" | "style") => {
          const response = await network.fetch(url, {
            cache: "no-store",
            credentials: "same-origin",
            signal,
          });
          const mime = response.headers
            .get("content-type")
            ?.split(";", 1)[0]
            ?.trim()
            .toLowerCase();
          const validType =
            type === "html"
              ? mime === "text/html"
              : type === "style"
                ? mime === "text/css"
                : mime?.includes("javascript") || mime?.includes("ecmascript");
          if (!response.ok || !validType) throw new RefreshUnavailable();
          const body = await response.text();
          if (!body.trim()) throw new RefreshUnavailable();
          return body;
        };
        const document = new DOMParser().parseFromString(
          await read(shell, "html"),
          "text/html",
        );
        const resources = new Map<string, "script" | "style">();
        let runtimeConfig = false;
        let appModule = false;
        for (const script of document.querySelectorAll<HTMLScriptElement>(
          "script[src]",
        )) {
          if (
            script.type &&
            script.type !== "module" &&
            !script.type.includes("javascript")
          )
            continue;
          const url = new URL(script.getAttribute("src")!, shell);
          // External diagnostics do not gate application startup.
          if (url.origin !== shell.origin) continue;
          if (url.pathname === "/__env.js") runtimeConfig = true;
          if (script.type === "module") appModule = true;
          resources.set(url.href, "script");
        }
        for (const link of document.querySelectorAll<HTMLLinkElement>(
          'link[rel="stylesheet"], link[rel="modulepreload"]',
        )) {
          const url = new URL(link.getAttribute("href") ?? "", shell);
          if (url.origin !== shell.origin) continue;
          resources.set(
            url.href,
            link.rel === "stylesheet" ? "style" : "script",
          );
        }
        if (!runtimeConfig || !appModule) throw new RefreshUnavailable();
        for (const [url, type] of resources) await read(new URL(url), type);
      } finally {
        network.close();
      }
    },
    catch: () => new RefreshUnavailable(),
  });
}

/** Observe a complete worker installation, including update races and scope control. */
function updateWorker(
  registration: ServiceWorkerRegistration,
  workers: ServiceWorkerContainer,
  href: () => string,
) {
  return Effect.suspend(() => {
    let cleanup = () => {};
    return Effect.async<void, RefreshUnavailable>((resume) => {
      let settled = false;
      const tracked = new Set<ServiceWorker>();
      let updated = false;
      let failed = false;
      const fail = () => {
        if (settled) return;
        failed = true;
        resume(Effect.fail(new RefreshUnavailable()));
      };
      const check = () => {
        if (settled) return;
        for (const candidate of [
          registration.installing,
          registration.waiting,
          registration.active,
        ]) {
          if (candidate && !tracked.has(candidate)) {
            tracked.add(candidate);
            candidate.addEventListener("statechange", check);
          }
        }
        if (
          [...tracked].some(
            (candidate) =>
              candidate.state === "redundant" && candidate !== originalActive,
          )
        )
          return fail();
        if (
          !updated ||
          failed ||
          registration.installing ||
          registration.waiting ||
          registration.active?.state !== "activated"
        )
          return;
        if (
          inWorkerScope(registration.scope, href()) &&
          workers.controller !== registration.active
        )
          return;
        resume(Effect.void);
      };
      const originalActive = registration.active;
      cleanup = () => {
        settled = true;
        registration.removeEventListener("updatefound", check);
        workers.removeEventListener("controllerchange", check);
        for (const worker of tracked)
          worker.removeEventListener("statechange", check);
      };
      registration.addEventListener("updatefound", check);
      workers.addEventListener("controllerchange", check);
      check();
      // Listeners are present before update(), then state is re-read after it resolves.
      void registration.update().then(() => {
        updated = true;
        check();
      }, fail);
    }).pipe(Effect.ensuring(Effect.sync(() => cleanup())));
  });
}

/** Called only by an explicit action. Never modifies storage or worker registration. */
export function makeBrowserRefresh(browser: RefreshBrowser) {
  let busy = false;
  return Effect.suspend(() => {
    if (busy) return Effect.void;
    busy = true;
    const prepare = Effect.gen(function* () {
      if (!browser.navigator.onLine)
        return yield* Effect.fail(new RefreshUnavailable());
      const workers = browser.navigator.serviceWorker;
      const registration = workers
        ? yield* Effect.tryPromise({
            try: () =>
              workers.getRegistration(
                new URL("/inspections/", browser.location.href).href,
              ),
            catch: () => new RefreshUnavailable(),
          })
        : undefined;
      const inspectionRegistration =
        registration &&
        new URL(registration.scope).pathname.replace(/\/$/, "") ===
          "/inspections"
          ? registration
          : undefined;
      if (workers && inspectionRegistration) {
        yield* updateWorker(
          inspectionRegistration,
          workers,
          () => browser.location.href,
        );
      }
      if (
        !inspectionRegistration ||
        !inWorkerScope(inspectionRegistration.scope, browser.location.href)
      ) {
        yield* prepareUncontrolledPage(browser);
      }
    }).pipe(
      Effect.timeoutFail({
        duration: "45 seconds",
        onTimeout: () => new RefreshUnavailable(),
      }),
    );
    return prepare.pipe(
      Effect.zipRight(
        Effect.sync(() => {
          // Ordinary reload preserves current query/fragment and native unload protection.
          browser.location.reload();
        }),
      ),
      Effect.ensuring(
        Effect.sync(() => {
          busy = false;
        }),
      ),
    );
  });
}
